import type { ReactNode } from 'react';
import { useEffect } from 'react';
import Head from 'next/head';
import getConfig from 'next/config';
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import { DFPSlotsProvider } from 'react-dfp';
import { GridIc, ListIc } from '@dsch/dd-icons';

import type {
  ISearchPageFilter,
  ISearchPageAds,
  ISearchPageBreadcrumbs,
  IDFP,
  IGTM,
  ISearchPageJatoAds,
  IGetSection,
  ISeoFaqSchema,
} from 'api/types/searchPageApiTypes';
import type { IAdSenseUnit } from 'types';
import type { ISeller } from 'api/types/sellerApiTypes';
import type { FeaturedDealer as IFeaturedDealier } from 'api/types/adviewApiTypes';
import type { ISearchPageSeoData } from 'api/types/searchPageApiTypes';
import type { NotificationFrequency } from 'components/SearchPage/hooks/useSavedSearch';
import type { UnitProps } from 'components/Toolkit/DFP';
import {
  BreadCrumb,
  BreadCrumbs,
} from 'components/Toolkit/BreadCrumbs/BreadCrumbs';
import { cookies } from 'constants/cookies';
import { adSenseSearchStyleId } from 'helpers/constants';
import {
  AD_UNIT_TYPE,
  AdUnit,
} from 'components/SearchPage/hooks/useSearchPageAdUnits';
import { getVehicleSchemaLD } from 'components/SearchPage/helpers/seo';
import { useNextRouter } from 'hooks/UseNextRouter';
import { useOnUpdateOnly } from 'hooks/UseOnUpdateOnly';
import { useCookies } from 'hooks/UseCookies';
import { useWootric } from 'hooks/UseWootric';
import { useScrollRestoration } from 'hooks/UseScrollRestoration';
import { useUserContext } from 'contexts/UserContext';
import { getSearchUrl } from 'components/SearchPage/helpers/url';
import { useSearchPageSeo } from 'components/SearchPage/hooks/useSearchPageSeo';
import { useSavedSearch } from 'components/SearchPage/hooks/useSavedSearch';
import { useSearchPageGTM } from 'components/SearchPage/hooks/useSearchPageGTM';
import { useSearchPageAdUnits } from 'components/SearchPage/hooks/useSearchPageAdUnits';
import { useAds } from 'components/SearchPage/hooks/useAds';
import { Filters } from 'components/SearchPage/components/Filters/Filters';
import { KeywordSearchBox } from 'components/SearchPage/components/Search/KeywordSearchBox';
import { Pagination } from 'components/Toolkit/Pagination/Pagination';
import { Sort } from 'components/SearchPage/components/Sort/Sort';
import { NoResults } from 'components/SearchPage/components/NoResults/NoResults';
import { SeoTags } from 'components/SearchPage/components/SeoTags/SeoTags';
import { StickyFooter } from 'components/Toolkit/StickyFooter/StickyFooter';
import { Columns } from 'components/Toolkit/Columns/Columns';
import { SaveSearchModal } from 'components/SearchPage/components/SaveSearchModal/SaveSearchModal';
import { FilterButton } from 'components/SearchPage/components/FilterButton/FilterButton';
import { SaveSearchButton } from 'components/SearchPage/components/SaveSearchButton/SaveSearchButton';
import { FiltersPanel } from 'components/SearchPage/components/FiltersPanel/FiltersPanel';
import { SectionBrowserPanel } from 'components/SearchPage/components/SectionBrowserPanel/SectionBrowserPanel';
import { SectionBrowserDropdown } from 'components/SearchPage/components/SectionBrowserDropdown/SectionBrowserDropdown';
import { DFPUnit, clearAdSlots } from 'components/Toolkit/DFP/DFP';
import { Listings } from 'components/SearchPage/components/Listings/Listings';
import { ListItemFullWidth } from 'components/SearchPage/components/Listings/Listings.styled';
import { Link } from 'components/Toolkit/Button/Link';
import { FeaturedDealer } from 'components/Toolkit/FeaturedDealer/FeaturedDealer';
import { BuyingCarTips } from 'components/SearchPage/components/BuyingCarTips/BuyingCarTips';
import { Faq } from 'components/SearchPage/components/Faq/Faq';
import { FiltersModal } from 'components/SearchPage/components/FiltersModal/FiltersModal';
import { RecommendedAdsCarousel } from 'components/SearchPage/components/RecommendedAdsCarousel/RecommendedAdsCarousel';

import * as SearchPageLayout from 'components/SearchPage/styles';

import {
  useSearchFilterDispatch,
  useSearchFilterState,
} from 'components/SearchPage/context/SearchFilterContext';
import {
  useSearchPageDispatch,
  useSearchPageState,
} from 'components/SearchPage/context/SearchPageContext';
import { useLastSearch } from 'components/PageWrappers/SharedComponents/LastSearchCard/hooks/useLastSearch';
import { useSectionDisplayInformation } from 'components/SearchPage/hooks/useSectionDisplayInformation';

import { pushToDataLayer } from 'services/gtmService';
import {
  filterBreadcrumbs,
  removeLinkFromLastBreadcrumb,
} from 'components/SearchPage/helpers/breadcrumbs';

import type { IHandleSelectSearchItem } from 'components/Toolkit/SearchModal/BaseSearch';
import { useDisplayFilters } from 'components/SearchPage/hooks/useDisplayFilters';
import {
  mapGeoFilter,
  mapFiltersRequest,
  mapRangesRequest,
  mapGeoFilterRequest,
  mapLegacyAreaFilterValues,
} from 'components/SearchPage/mappers/filters';
import { useLocationContext } from 'features/location/Location.context';
import { TrustedDealerModal } from 'components/SearchPage/TrustedDealerModal/TrustedDealerModal';
import { useAuthOptions } from 'hooks/useAuthOptions';
import { useLocationSearchTracking } from 'components/SearchPage/hooks/useLocationSearchTracking/useLocationSearchTracking';

export interface ISearchPageProps {
  initialValues: {
    sectionsData: IGetSection[];
    sectionName: string;
    parentName: string | null;
    parentDisplayName: string | null;
    filtersData: ISearchPageFilter[];
    ads: ISearchPageAds[];
    jatoAds: ISearchPageJatoAds[];
    spotlights: ISearchPageAds[];
    seoData: ISearchPageSeoData;
    dfp: IDFP;
    gtm: IGTM;
    faq: ISeoFaqSchema | null;
    searchMatches: number;
    recommendations: ISearchPageAds[];
  };
  trader: ISeller | null;
  promoSlot?: IFeaturedDealier | null;
  notFound: boolean;
}

export type ViewType = 'grid' | 'list';

const {
  publicRuntimeConfig: {
    CDN_STATIC_ASSETS,
    DFP_NETWORK_ID,
    AD_SENSE_PUBLIC_KEY,
    MIGRATED_SEARCH_PAGES,
    ENVIRONMENT_NAME,
  },
} = getConfig();

function SearchPage({
  initialValues,
  notFound,
  trader,
  promoSlot,
}: ISearchPageProps) {
  const { updateUrlByHref, router } = useNextRouter();
  useScrollRestoration(router);

  const { accessToken } = useAuthOptions();

  const {
    filters,
    ranges,
    appliedFiltersRangeCount,
    makeModel,
    isFilterModalOpen,
  } = useSearchFilterState();

  const isPrivateSellerAd = filters
    .find((filter) => filter.name === 'sellerType')
    ?.values.includes('private');

  const {
    getAreas,
    getYears,
    getFuelTypes,
    getColours,
    getBodyTypes,
    getTransmissions,
    getMakes,
    getModels,
    getWanted,
    setIsFilterModalOpen,
    setFilters,
    reset,
    addMakeModelWithKeywordSearch,
    setFilterAndRangeValues,
    populateMakeModelFilterWithOptions,
  } = useSearchFilterDispatch();

  const {
    keywordSearchTerm,
    page,
    sort,
    section,
    viewType,
    baseUrl,
    userId,
    paging,
    breadcrumbs,
    recommendations,
  } = useSearchPageState();
  const isCars = section === 'cars';

  const {
    setKeywordSearchTerm,
    setSort,
    setPageByDividing,
    setPageInitial,
    setPageByTotal,
    setViewType,
    setSection,
    setPaging,
    setOnSearchSuccess,
  } = useSearchPageDispatch();

  const viewTypeCookie = useCookies({ name: cookies.VIEW_TYPE });
  const {
    user,
    consentOptions,
    isSaveSearchModalOpen,
    setIsSaveSearchModalOpen,
    isInstantAlertAvailable,
    setIsTrustedDealerModalOpen,
    isTrustedDealerModalOpen,
  } = useUserContext();

  const {
    ads,
    spotlights,
    jatoAds,
    isLoading,
    seoData,
    dfp: dfpSettings,
    gtm: gtmData,
    fetchAds,
    combineAds,
  } = useAds(
    initialValues.ads,
    initialValues.spotlights,
    initialValues.jatoAds,
    initialValues.seoData,
    initialValues.dfp,
    initialValues.gtm,
    setPaging,
  );
  const combinedAds = combineAds(ads, spotlights, jatoAds);

  const {
    sections,
    sectionName,
    parentName,
    parentDisplayName,
    retrieveSections,
    searchMatches,
  } = useSectionDisplayInformation({
    initialSections: initialValues.sectionsData,
    initialSectionName: initialValues.sectionName,
    initialParentName: initialValues.parentName,
    initialParentDisplayName: initialValues.parentDisplayName,
    initialSearchMatches: initialValues.searchMatches,
  });

  const adUnits = useSearchPageAdUnits({
    dfpSettings,
    advertisingConsent: Boolean(consentOptions?.advertising),
    adSenseSettings: {
      query: keywordSearchTerm || sectionName || 'cars',
      pubId: AD_SENSE_PUBLIC_KEY,
      styleId: adSenseSearchStyleId,
    },
  });

  const gtm = useSearchPageGTM(gtmData, Boolean(consentOptions?.analytics));

  const {
    handleOnClickSave,
    createSavedSearch,
    saving: savedSearchSaving,
    savedSearchTerms,
    savedSearchName,
    isSavedSearchDisabled,
  } = useSavedSearch(sectionName, accessToken);

  const DFPVertical = dfpSettings?.vertical[0] || '';
  useWootric(
    user?.id,
    mapDFPVertical(DFPVertical),
    user?.registrationdate || '',
    ENVIRONMENT_NAME === 'production',
  );

  const {
    selectedCounty,
    coordinates,
    radius,
    countyTown,
    isLocationBottomSheetOpen,
    isExpanded: isLocationDropdownExpanded,
  } = useLocationContext();
  const { latitude, longitude } = coordinates;
  const county = selectedCounty?.value ?? null;
  const countyDisplayName = selectedCounty?.displayName;
  const legacyAreas = getAreas();
  const areas =
    legacyAreas.length > 0 ? legacyAreas : county ? [county] : undefined;

  useLocationSearchTracking({
    isLocationBottomSheetOpen,
    isLocationDropdownExpanded,
    county: countyDisplayName,
    countyTown,
    radius,
  });

  const { isMotor, h1, h2, pageTitle, metaDescription, canonical } =
    useSearchPageSeo({
      baseURL: baseUrl,
      areas,
      sectionName,
      adTotal: paging.totalResults,
      wanted: getWanted(),
      trader,
      years: getYears(),
      fuels: getFuelTypes(),
      makes: getMakes(),
      models: getModels(),
      colours: getColours(),
      transmissions: getTransmissions(),
      bodyTypes: getBodyTypes(),
      countyTown,
      radius: countyTown ? radius : null,
    });

  const { filtersData } = useDisplayFilters({
    initialFiltersData: initialValues.filtersData,
    section,
    setFilterAndRangeValues,
    queryParams: router.query,
    userId,
    populateMakeModelFilterWithOptions,
  });

  const previousSection =
    parentName && parentDisplayName
      ? {
          name: parentName,
          displayName: parentDisplayName,
        }
      : null;

  const { setLastSearch, lastSearchData } = useLastSearch();
  const tdpModalSeenCookie = useCookies({ name: cookies.TDP_MODAL_SEEN });

  useOnUpdateOnly(() => {
    if (consentOptions?.functional && viewType) {
      viewTypeCookie.set({ value: viewType });
    }
  }, [viewType]);

  useOnUpdateOnly(() => {
    setPageInitial();
  }, [filters, ranges]);

  useOnUpdateOnly(() => {
    clearAdSlots();
    const locationData = mapGeoFilter({
      radius,
      latitude,
      longitude,
      county,
      countyTown,
    });
    fetchAds(
      filters,
      ranges,
      makeModel,
      page,
      paging.pageSize,
      section,
      keywordSearchTerm,
      sort,
      userId,
      () => undefined,
      (
        breadcrumbs: ISearchPageBreadcrumbs[],
        recommendations?: ISearchPageAds[],
      ) => {
        const { url } = buildSeoParams();
        updateUrlByHref(url);
        setOnSearchSuccess(breadcrumbs, recommendations ?? []);
      },
      locationData?.geoFilter,
    );
    gtm.searchPageEvent({ viewType: [viewType] });
  }, [
    filters,
    ranges,
    sort,
    keywordSearchTerm,
    page,
    makeModel,
    section,
    countyTown,
    selectedCounty,
    radius,
  ]);

  const handleTrustedDealerModal = () => {
    if (tdpModalSeenCookie.get()) {
      return;
    }

    setIsTrustedDealerModalOpen(true);
  };

  useOnUpdateOnly(() => {
    adUnits.refresh();
  }, [dfpSettings]);

  useOnUpdateOnly(() => {
    window.scroll({ top: 0 });
  }, [page]);

  useEffect(() => {
    gtm.searchPageEvent({ viewType: [viewType] });

    if (isCars && !isPrivateSellerAd) {
      handleTrustedDealerModal();
    } else {
    }
  }, []);

  function mapDFPVertical(name: string) {
    switch (name) {
      case 'All': {
        return 'Marketplace';
      }
      default: {
        return name;
      }
    }
  }

  const mapBreadcrumbs = (breadcrumbs: ISearchPageBreadcrumbs[]) => {
    const { seoBreadcrumbs } = buildSeoParams();
    const FILTER_TERM = 'Cars & Motor';
    const baseBreadCrumbs = [
      { displayName: 'Home', path: '/' },
      ...(breadcrumbs &&
        filterBreadcrumbs(breadcrumbs, FILTER_TERM)?.map((item) => ({
          path: buildUrl(`${item.name}`),
          displayName: item.displayName,
        }))),
    ];
    const combinedBreadcrumbs = seoBreadcrumbs
      ? [...baseBreadCrumbs, ...seoBreadcrumbs]
      : (baseBreadCrumbs as BreadCrumb[]);
    return removeLinkFromLastBreadcrumb(combinedBreadcrumbs);
  };

  const buildUrl = (baseUrl: string) => {
    const url =
      baseUrl.slice(-1) === '&' || baseUrl.slice(-1) === '?'
        ? baseUrl.slice(0, -1)
        : baseUrl;

    return !MIGRATED_SEARCH_PAGES.includes(section)
      ? `/search/${url}`
      : `/${url}`;
  };
  function buildSeoParams() {
    const validRadius = countyTown ? radius : undefined;

    const { url, breadCrumbs } = getSearchUrl(
      section,
      {
        ...(userId && { userId }),
        ...(sort && { sort }),
        ...(keywordSearchTerm && { words: keywordSearchTerm }),
        ...(page && { start: (page * paging.pageSize).toString() }),
      },
      filters,
      makeModel,
      ranges,
      countyTown,
      validRadius,
      county,
    );

    return {
      url,
      seoBreadcrumbs: breadCrumbs,
    };
  }

  useOnUpdateOnly(() => {
    const { url } = buildSeoParams();
    setLastSearch(savedSearchTerms, url);
  }, [savedSearchTerms]);

  useOnUpdateOnly(() => {
    const geoFilterCounty = county ? [county] : null;
    const locationData = mapGeoFilter({
      radius,
      latitude,
      longitude,
      county: geoFilterCounty,
      countyTown,
    });
    if (!!sections?.length) {
      const mappedFilterValues = mapLegacyAreaFilterValues(
        filters,
        locationData?.geoFilter?.county,
        locationData?.geoFilter?.countyTown,
      );

      retrieveSections(section, {
        geoFilter: mapGeoFilterRequest(locationData?.geoFilter),
        filters: mapFiltersRequest([...mappedFilterValues]),
        ...(ranges.length && { ranges: mapRangesRequest(ranges) }),
        paging: {
          pageSize: paging.pageSize,
          from: page * paging.pageSize,
        },
        sections: [section],
        terms: keywordSearchTerm ? keywordSearchTerm : null,
        sort,
      });
    }
  }, [
    filters,
    ranges,
    keywordSearchTerm,
    section,
    selectedCounty,
    radius,
    countyTown,
  ]);

  async function handleOnSubmitSave(
    notifications: NotificationFrequency,
    name: string,
  ) {
    await createSavedSearch({
      name,
      description: savedSearchTerms,
      notifications,
      userId: user?.id || 0,
      keywordSearchTerm,
      sort,
      section,
      ranges: ranges,
      filters: filters,
      makeModel: makeModel,
    });
    gtm.createSavedSearchEvent(notifications);
  }

  function onClickSuggestion({
    autoSuggestedText,
    searchRequest,
  }: IHandleSelectSearchItem) {
    let searchType;
    const searchRequestMakeAndModel =
      searchRequest?.makeModelFilters && searchRequest?.makeModelFilters[0];
    if (searchRequestMakeAndModel) {
      const searchRequestMake = searchRequestMakeAndModel.make;
      const searchRequestModel = searchRequestMakeAndModel?.model
        ? [searchRequestMakeAndModel.model]
        : undefined;
      reset();
      addMakeModelWithKeywordSearch(searchRequestMake, searchRequestModel);
      searchType = 'section_structured_search';
    } else if (searchRequest?.filters) {
      reset();
      searchRequest.filters && setFilters(searchRequest.filters);
      searchType = 'section_structured_search';
    } else {
      setKeywordSearchTerm(autoSuggestedText);
      searchType = 'section_keyword_search';
    }
    pushToDataLayer({
      event: 'keyword_search',
      searchType,
      searchTerm: autoSuggestedText,
    });
  }

  function renderAdSenseUnit(
    item: IAdSenseUnit,
    index: number,
    hidden: boolean,
  ) {
    return (
      <ListItemFullWidth
        className={`ad-sense-${item.container}`}
        key={`ad-sense-${index}`}
        id={item.container}
        hidden={hidden}
      />
    );
  }

  function renderDFPUnit(item: UnitProps, index: number, hidden: boolean) {
    return (
      <ListItemFullWidth
        className={`dfp-${item.slotId}`}
        key={`dfp-${index}`}
        hidden={hidden}
      >
        <DFPUnit onSlotRender={adUnits.onDFPUnitLoad} {...item} />
      </ListItemFullWidth>
    );
  }

  function renderRecommendedAdsCarousel(recommendedAds: ISearchPageAds[]) {
    return (
      <ListItemFullWidth key="recommended_ads_carousel">
        <RecommendedAdsCarousel recommendedAds={recommendedAds} />
      </ListItemFullWidth>
    );
  }

  function renderListingExtraUnits(
    index: number,
    viewType: ViewType | undefined,
  ): ReactNode {
    const item = adUnits.getListingUnit(index, viewType);

    const isGrid = viewType === 'grid';

    const RECOMMENDED_ADS_LISTING_VIEW_POSITION = 11;
    const RECOMMENDED_ADS_GRID_VIEW_LISTINGS_POSITION = 12;

    const renderRecommendedAdsListingsView =
      !isGrid &&
      index === RECOMMENDED_ADS_LISTING_VIEW_POSITION &&
      recommendations &&
      recommendations.length > 0;

    const renderRecommendedAdsGridView =
      isGrid &&
      index === RECOMMENDED_ADS_GRID_VIEW_LISTINGS_POSITION &&
      recommendations &&
      recommendations.length > 0;

    if (renderRecommendedAdsListingsView) {
      return renderRecommendedAdsCarousel(recommendations);
    }

    if (renderRecommendedAdsGridView) {
      return renderRecommendedAdsCarousel(recommendations);
    }

    if (!item) {
      return [];
    }

    switch (item.type) {
      case AD_UNIT_TYPE.AD_SENSE:
        if (!isCars) {
          return renderAdSenseUnit(item.data, index, item.hidden || false);
        } else return [];
      case AD_UNIT_TYPE.DFP:
        return renderDFPUnit(item.data, index, item.hidden || false);
      default:
        return [];
    }
  }

  function renderBottomUnit(item: AdUnit) {
    switch (item.type) {
      case AD_UNIT_TYPE.AD_SENSE:
        return <div id={item.data.container}></div>;
      case AD_UNIT_TYPE.DFP:
        return <DFPUnit {...item.data} />;
      default:
        return [];
    }
  }

  const isVehicleSchemaSection =
    isMotor || section === 'vintagecars' || section === 'tractors';

  const mappedBreadcrumbs = mapBreadcrumbs(breadcrumbs);

  const breadcrumbJsonLdItems = mappedBreadcrumbs.map((breadcrumb, index) => {
    return {
      position: index + 1,
      name: breadcrumb.displayName as string,
      item: `${baseUrl}${breadcrumb.path!}`,
    };
  });

  const hasAds = combinedAds && combinedAds.length > 0;

  const getMakeModelName = () => {
    const makes = getMakes();
    const models = getModels();
    const hasMake = isCars && makes.length === 1;
    const hasMakeModel = hasMake && models.length === 1;
    if (hasMake && !hasMakeModel) return makes[0];
    else if (hasMake && hasMakeModel) return `${makes[0]} ${models[0]}`;
    else return null;
  };

  const showBuyingCarTips = isCars && mappedBreadcrumbs.length === 3;

  const SearchHead = () => {
    return (
      <>
        {isVehicleSchemaSection && hasAds && (
          <Head>
            <script type="application/ld+json">
              {getVehicleSchemaLD(combinedAds, section, h1)}
            </script>
          </Head>
        )}
        <NextSeo
          title={pageTitle}
          description={metaDescription}
          canonical={canonical}
          nofollow={!hasAds}
          noindex={!hasAds}
          openGraph={{
            url: canonical,
            type: 'product',
            title: pageTitle,
            description: metaDescription,
            ...(combinedAds[0] &&
              'photos' in combinedAds[0] &&
              combinedAds[0].photos &&
              'large' in combinedAds[0].photos[0] && {
                images: [
                  {
                    url: combinedAds[0].photos[0].large,
                    width: 600,
                    height: 450,
                  },
                ],
              }),
          }}
        />
      </>
    );
  };

  return (
    <>
      <SearchPageLayout.Container>
        <SearchHead />
        <BreadcrumbJsonLd itemListElements={breadcrumbJsonLdItems} />
        <SearchPageLayout.Header>
          <div className="row">
            <div className="columns small-12">
              <SearchPageLayout.Breadcrumbs>
                <BreadCrumbs
                  baseUrl={baseUrl}
                  breadcrumbs={mappedBreadcrumbs}
                  variant="COMPACT"
                />
              </SearchPageLayout.Breadcrumbs>
              <SearchPageLayout.HeaderDivider>
                <SearchPageLayout.HeaderTitle>
                  {h1}
                </SearchPageLayout.HeaderTitle>
                <SearchPageLayout.HeaderSearch>
                  <KeywordSearchBox
                    section={section}
                    sectionName={sectionName}
                    onSearch={setKeywordSearchTerm}
                    onClickSuggestion={onClickSuggestion}
                    presetInput={keywordSearchTerm}
                    placeholder={`Search ${sectionName}`}
                  />
                </SearchPageLayout.HeaderSearch>
                {!!sections?.length && (
                  <SearchPageLayout.HeaderSections data-testid="section-dropdown">
                    <SectionBrowserDropdown
                      currentSection={{
                        name: section,
                        displayName: sectionName,
                        searchMatches,
                      }}
                      sections={sections}
                      parentSection={previousSection}
                      onSelectSection={setSection}
                      onSelectBack={setSection}
                    />
                  </SearchPageLayout.HeaderSections>
                )}
              </SearchPageLayout.HeaderDivider>
            </div>
          </div>
        </SearchPageLayout.Header>
        <DFPSlotsProvider
          dfpNetworkId={DFP_NETWORK_ID}
          autoLoad={true}
          limitedAds={!Boolean(consentOptions?.advertising)}
          cookieOption={Boolean(consentOptions?.advertising)}
        >
          <div className="row">
            <div className="columns small-12">
              <SearchPageLayout.DFPUnitContainer_Top>
                <DFPUnit {...adUnits.getTopUnit()} />
              </SearchPageLayout.DFPUnitContainer_Top>
            </div>
          </div>
          <div className="row">
            <div className="columns small-12">
              <SearchPageLayout.Main>
                <SearchPageLayout.Sidebar>
                  <SaveSearchButton
                    disabled={isSavedSearchDisabled}
                    onClick={handleOnClickSave}
                  />{' '}
                  <FiltersPanel lastSearchData={lastSearchData}>
                    {!!sections?.length && (
                      <SectionBrowserPanel
                        currentSection={{
                          name: section,
                          displayName: sectionName,
                          searchMatches,
                        }}
                        sections={sections}
                        parentSection={previousSection}
                        onSelectSection={setSection}
                        onSelectBack={setSection}
                      />
                    )}
                    <Filters section={section} filtersData={filtersData} />
                  </FiltersPanel>
                  <SearchPageLayout.SidebarAdUnit>
                    <DFPUnit {...adUnits.getSidebarUnit()} />
                  </SearchPageLayout.SidebarAdUnit>
                </SearchPageLayout.Sidebar>
                <SearchPageLayout.Body>
                  <SearchPageLayout.DetailsAndToggles>
                    <SearchPageLayout.Details data-testid="h2-details-text">
                      {h2}
                    </SearchPageLayout.Details>
                    {ads?.length ? (
                      <SearchPageLayout.Toggles>
                        <SearchPageLayout.ViewTypeButtons>
                          <SearchPageLayout.ViewTypeButton
                            active={viewType === 'list'}
                            title="List View"
                            onClick={() => setViewType('list')}
                          >
                            <ListIc />
                          </SearchPageLayout.ViewTypeButton>
                          <SearchPageLayout.ViewTypeButton
                            active={viewType === 'grid'}
                            title="Grid View"
                            onClick={() => setViewType('grid')}
                          >
                            <GridIc />
                          </SearchPageLayout.ViewTypeButton>
                        </SearchPageLayout.ViewTypeButtons>
                        <SearchPageLayout.Sort>
                          <Sort selected={sort} onChange={setSort} />
                        </SearchPageLayout.Sort>
                      </SearchPageLayout.Toggles>
                    ) : null}
                  </SearchPageLayout.DetailsAndToggles>
                  {notFound && (
                    <NoResults
                      headerText="Sorry, this ad is no longer available"
                      subText="View similar ads below"
                      imageSrc={`${CDN_STATIC_ASSETS}/images/illustrations/ad-review.svg`}
                    />
                  )}
                  {hasAds ? (
                    <>
                      <Listings
                        listings={combinedAds}
                        section={section}
                        createSavedAdEvent={gtm.createSavedAdEvent}
                        deleteSavedAdEvent={gtm.deleteSavedAdEvent}
                        isLoading={isLoading}
                        viewType={viewType}
                        extra={(index) => [
                          renderListingExtraUnits(index, viewType),
                        ]}
                      />
                      {paging && (
                        <SearchPageLayout.Pagination>
                          <Pagination
                            currentPage={paging.currentPage}
                            totalPages={paging.totalPages}
                            totalResults={paging.totalResults}
                            onUpdate={async (event) =>
                              setPageByDividing(event.from, paging.pageSize)
                            }
                            pageSize={paging.pageSize}
                            searchQueryGroup=""
                            goToEnd={() => setPageByTotal(paging.totalPages)}
                            goToStart={() => setPageByTotal(1)}
                          />
                        </SearchPageLayout.Pagination>
                      )}
                    </>
                  ) : (
                    <NoResults
                      headerText="Sorry, we couldn't find what you were looking for"
                      subText={
                        <>
                          Make sure any search words are spelled correctly or
                          search{' '}
                          <Link
                            href={`/all?words=${keywordSearchTerm}`}
                            ofType="SECONDARY"
                            inline
                          >
                            All Sections
                          </Link>
                        </>
                      }
                    />
                  )}

                  {!!seoData?.internalLinks?.length &&
                    seoData?.type !== 'popular' && (
                      <SearchPageLayout.InternalTagsPanel>
                        <SearchPageLayout.InternalTagsTitle>
                          You may also be interested in
                        </SearchPageLayout.InternalTagsTitle>
                        <SeoTags items={seoData?.internalLinks} />
                      </SearchPageLayout.InternalTagsPanel>
                    )}

                  {Boolean(consentOptions?.advertising) &&
                    renderBottomUnit(adUnits.getBottomUnit())}
                </SearchPageLayout.Body>
              </SearchPageLayout.Main>
            </div>
          </div>
        </DFPSlotsProvider>
        <SearchPageLayout.StickyFooter>
          <StickyFooter data-testid="search-sticky-footer">
            <Columns>
              {!isSavedSearchDisabled && (
                <SaveSearchButton onClick={handleOnClickSave} />
              )}
              <FilterButton
                filterCount={appliedFiltersRangeCount}
                onClick={() => {
                  window.scroll({ top: 0 });
                  setIsFilterModalOpen(true);
                }}
              />
            </Columns>
          </StickyFooter>
        </SearchPageLayout.StickyFooter>
      </SearchPageLayout.Container>

      {showBuyingCarTips && (
        <SearchPageLayout.BuyingCarTipsContainer>
          <div className="row small-collapse medium-uncollapse">
            <div className="columns small-12">
              <BuyingCarTips
                baseUrl={baseUrl}
                blogUrl="https://blog.donedeal.ie"
                youtubeUrl="https://www.youtube.com/channel/UCNYw8CUPcrAEy9TbHV2Pr5A"
              />
            </div>
          </div>
        </SearchPageLayout.BuyingCarTipsContainer>
      )}

      {!!seoData?.internalLinks?.length && seoData?.type === 'popular' && (
        <SearchPageLayout.InternalLinksContainer isWhite={!isCars}>
          <div className="row small-collapse medium-uncollapse">
            <div className="columns small-12">
              <SearchPageLayout.SInternalLinks
                title="You may also be interested in"
                links={seoData.internalLinks}
              />
            </div>
          </div>
        </SearchPageLayout.InternalLinksContainer>
      )}

      {initialValues.faq && (
        <Faq {...initialValues.faq} makeModel={getMakeModelName()} />
      )}

      {promoSlot && (
        <SearchPageLayout.FeaturedDealerContainer>
          <div className="row small-collapse medium-uncollapse">
            <div className="columns small-12">
              <FeaturedDealer
                heading="Featured Dealer"
                domain={baseUrl}
                cdnUrl={CDN_STATIC_ASSETS}
                dealerLogo={promoSlot.largeLogoUrl ?? promoSlot.logoUrl}
                title={promoSlot.display}
                listItems={[
                  promoSlot.dealerType,
                  `Total Stock: ${promoSlot.totalAdCount} Ads`,
                ]}
                showRoomUrl={promoSlot.showroomUrl}
                showRoomText="See Showroom"
                ads={promoSlot.ads}
                testId="featured-dealer-list"
              />
            </div>
          </div>
        </SearchPageLayout.FeaturedDealerContainer>
      )}
      {isFilterModalOpen && (
        <FiltersModal
          section={section}
          filtersData={filtersData}
          adCount={paging?.totalResults || 0}
          lastSearchData={lastSearchData}
          lastSearchCallback={() => {
            setIsFilterModalOpen(false);
          }}
        />
      )}
      {isSaveSearchModalOpen && (
        <SaveSearchModal
          saving={savedSearchSaving}
          name={savedSearchName}
          terms={savedSearchTerms}
          isOpen={isSaveSearchModalOpen}
          askToClose={() => setIsSaveSearchModalOpen(false)}
          onSave={handleOnSubmitSave}
          cdnUrl={CDN_STATIC_ASSETS}
          isInstantAlertAvailable={isInstantAlertAvailable}
        />
      )}
      {isTrustedDealerModalOpen && (
        <TrustedDealerModal
          isOpen={isTrustedDealerModalOpen}
          askToClose={() => setIsTrustedDealerModalOpen(false)}
        />
      )}
    </>
  );
}

export { SearchPage };
